// src/pages/CmsUsers.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../../utils/cmsAxios";
import SortableTable from "../CommonComponents/SortableTable";
import PeriodPicker, { toKstIsoRange } from "../CommonComponents/PeriodPicker";
import RowActions from "../CommonComponents/RowActions";
import DeleteModal from "./DeleteModal";
import StopModal from "./StopModal";
import UnstopModal from "./UnstopModal";
import { stateLabel } from "./UserStateModal";
import { Calendar } from "lucide-react";

const CHANNEL_LABELS = {
  kakao: "카카오톡",
  google: "구글",
  naver: "네이버",
  apple: "애플",
  local: "로컬",
};

const TICKET_NAME_MAP = [
  "전체",
  "스탠다드 플랜",
  "프리미엄 플랜",
  "컨시어지 플랜",
  "베이직 플랜",
];

const APPLIED_DEFAULT = {
  searchField: "all",
  keyword: "",
  channel: "all",
  joinStart: "",
  joinEnd: "",
  ticketName: "전체",
};

const CmsUsersDeleted = () => {
  const navigate = useNavigate();

  // 🔎 검색/필터 상태
  const [searchField, setSearchField] = useState("all");
  const [keyword, setKeyword] = useState("");   // user_id 입력(숫자) 기준
  const [channel, setChannel] = useState("all"); // all | kakao | google
  // const [product, setProduct] = useState("all"); // all | standard | premium | concierge | basic
  const [users, setUsers] = useState([]);
  const [ticketName, setTicketName] = useState("전체");

  const [showJoinPicker, setShowJoinPicker] = useState(false);
  const [joinMode, setJoinMode] = useState("single"); // 'single' | 'range'
  const [joinSingle, setJoinSingle] = useState(null); // Date | null
  const [joinRange, setJoinRange] = useState({ from: null, to: null }); // {from,to}

  const [loading, setLoading] = useState(false);

  const PAGE_SIZE = 10;
  const BLOCK_SIZE = 10;
  const [page, setPage] = useState(1);

  // 회원 상세 조회 API 재사용
  const getUserDetail = useCallback(async (userId) => {
    if (!userId) return null;
    const { data } = await axios.get(`${process.env.REACT_APP_FASTAPI_ADS_URL}/cms/get/deleted/user/detail`, { params: { user_id: userId } });
    return data?.detail ?? data ?? null;
  }, []);

  // 상태
  const [stopOpen, setStopOpen] = useState(false);
  const [stopTarget, setStopTarget] = useState(null);
  const [unstopOpen, setUnstopOpen] = useState(false);
  const [unstopTarget, setUnstopTarget] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const onStop = useCallback((row) => { setStopTarget(row); setStopOpen(true); }, []);
  const onUnstop = useCallback((row) => { setUnstopTarget(row); setUnstopOpen(true); }, []);
  const onDelete = useCallback((row) => { setDeleteTarget(row); setDeleteOpen(true); }, []);

  // 구독 상태 라벨 공통 함수
  const getSubLabel = (type) => {
    if (!type) return "해지";
    const v = String(type).toLowerCase();
    if (v === "standard" || v === "premium") return "구독중";
    if (v === "basic") return "단건결제";
    return "-";
  };

  // 정지 여부 판별
  const isRowStopped = (r) => {
    const s = String(r?.status || r?.user_state || "").toLowerCase();
    return s === "stop" || r?.user_state_label === "정지회원";
  };


  const getCurrentJoinParams = useCallback(() => {
    if (joinMode === "single" && joinSingle) {
      return toKstIsoRange(joinSingle, joinSingle);
    }
    if (joinMode === "range" && joinRange.from) {
      return toKstIsoRange(joinRange.from, joinRange.to || joinRange.from);
    }
    return {};
  }, [joinMode, joinSingle, joinRange]);

  const [applied, setApplied] = useState({
    searchField: "all",
    keyword: "",
    channel: "all",
    ticketName: "전체",
    // joinDate: "",
    joinStart: "",
    joinEnd: "",
  });


  // const formatDate = useCallback((dateString) => {
  //   const d = new Date(dateString);
  //   if (Number.isNaN(d.getTime())) return "";
  //   const y = d.getFullYear();
  //   const m = String(d.getMonth() + 1).padStart(2, "0");
  //   const dd = String(d.getDate()).padStart(2, "0");
  //   return `${y}-${m}-${dd}`;
  // }, []);

  const withDateTooltip = useCallback((v) => {
    if (!v) return "-";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "-";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    const tip = `${y}.${m}.${dd} ${hh}:${mm}:${ss}`;
    return <time dateTime={d.toISOString()} title={tip}>{`${y}-${m}-${dd}`}</time>;
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_FASTAPI_ADS_URL}/cms/get/deleted/user/list`;
      const { data } = await axios.get(url);
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("회원 정보를 불러오지 못했습니다", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const getUserCode = useCallback((u) => {
    if (!u) return "-";
    if (u.user_code) return u.user_code;
    return u.user_id != null ? `U-${String(u.user_id).padStart(7, "0")}` : "-";
  }, []);

  const filteredUsers = useMemo(() => {
    const byId = new Map();
    for (const u of (users || [])) {
      const prev = byId.get(u.user_id);
      if (!prev) { byId.set(u.user_id, u); continue; }
      const prevTime = new Date(prev.last_seen || prev.created_at || 0).getTime();
      const currTime = new Date(u.last_seen || u.created_at || 0).getTime();
      if (currTime > prevTime) byId.set(u.user_id, u);
    }
    const list = Array.from(byId.values());
    const q = (applied.keyword || "").trim().toLowerCase();

    const hasJoinRange = Boolean(applied.joinStart && applied.joinEnd);
    const startMs = hasJoinRange ? new Date(applied.joinStart).getTime() : null;
    const endMs = hasJoinRange ? new Date(applied.joinEnd).getTime() : null;

    return list.filter((u) => {
      if (applied.channel !== "all") {
        const lp = String(u.login_provider || "").toLowerCase();
        if (lp !== applied.channel) return false;
      }

      // 상품명 필터
      if (applied.ticketName !== "all" && applied.ticketName !== "전체") {
        const name = (u.ticket_name || "").trim();
        if (name !== applied.ticketName) return false;
      }

      // 가입일(단일 동일 일자 매칭)
      if (hasJoinRange) {
        const createdMs = new Date(u.created_at || 0).getTime();
        if (!(createdMs >= startMs && createdMs <= endMs)) return false;
      }

      // 검색 필드 필터
      if (!q || applied.searchField === "all") return true;
      if (applied.searchField === "user_id") return String(u.user_id).toLowerCase().includes(q);
      if (applied.searchField === "ticket_name") return String(u.ticket_name || "").toLowerCase().includes(q);
      if (applied.searchField === "nickname") return String(u.nickname || "").toLowerCase().includes(q);
      return true;
    });
  }, [users, applied]);

  const isDirty = useMemo(() => {
    const cur = getCurrentJoinParams();
    const curStart = cur.join_start || "";
    const curEnd = cur.join_end || "";
    return !(
      searchField === applied.searchField &&
      (keyword || "") === (applied.keyword || "") &&
      channel === applied.channel &&
      ticketName === applied.ticketName &&
      (curStart || "") === (applied.joinStart || "") &&
      (curEnd || "") === (applied.joinEnd || "")
    );
  }, [searchField, keyword, channel, ticketName, applied, getCurrentJoinParams]);

  const maskEmail = (email) => {
    if (typeof email !== "string" || email.length === 0) return "";
    const [local, domain] = email.split("@");
    if (!domain) {
      return local.length <= 1 ? local : local[0] + "*".repeat(local.length - 1);
    }
    if (local.length <= 1) return `${local}@${domain}`;
    return `${local[0]}${"*".repeat(local.length - 1)}@${domain}`;
  };

  // const cleanTicketName = (name = "") => {
  //   let s = String(name || "");
  //   s = s.replace(/\s*단품\s*/g, " ");            // “단품” 제거
  //   s = s.replace(/\s*\(마케팅\s*대행\)\s*/g, " "); // “(마케팅 대행)” 제거
  //   s = s.replace(/\s+/g, " ").trim();            // 공백 정리
  //   return s;
  // };


  // 컬럼 정의
  const columns = useMemo(() => ([
    // { key: "_rownum", header: "번호", type: "number", sortable: true, render: (_v, _r, index) => index + 1 },
    { key: "user_id", header: "회원번호", type: "number", sortable: true, sortBy: (row) => row.user_id, render: (_v, row) => getUserCode(row) },
    { key: "login_provider", header: "채널", type: "string", sortable: true, render: (v) => <span>{CHANNEL_LABELS[v] ?? "-"}</span> },
    { key: "email", header: "소셜 계정", type: "string", sortable: true, render: (v) => <span className="line-clamp-1">{maskEmail(v)}</span> },
    { key: "nickname", header: "이름", type: "string", sortable: true, render: (v) => <span className="line-clamp-1">{v}</span> },
    {
      key: "status", header: "회원상태", type: "string", sortable: false,
      render: (_v, row) => {
        const s = row?.status ?? row?.user_state ?? row?.user_state_label;
        const label = stateLabel(s); // UserStateModal에서 named export한 함수
        return <span>{label}</span>;
      },
    },
    // {
    //   key: "ticket_name", header: "상품명", type: "string", sortable: true,
    //   sortBy: (row) => cleanTicketName(row.ticket_name),
    //   render: (v) => <span className="line-clamp-1">{cleanTicketName(v)}</span>,
    // },
    { key: "created_at", header: "가입일", type: "date", sortable: true, render: withDateTooltip },
    { key: "last_seen", header: "최근 로그인", type: "date", sortable: true, render: withDateTooltip },
    { key: "last_seen1", header: "휴먼 전환일", type: "date", sortable: true, render: withDateTooltip },
    {
      key: "actions", header: "", sortable: false,
      cellClassName: "p-0 pr-0 text-center overflow-visible",
      render: (_v, row) => (
        <RowActions
          items={[
            isRowStopped(row)
              ? { key: "unstop", label: "회원 정지 해제", onClick: () => onUnstop(row) }
              : { key: "stop", label: "회원 정지", onClick: () => onStop(row) },
            { key: "delete", label: "회원 삭제", danger: true, onClick: () => onDelete(row) },
          ]}
        />),
    },
  ]), [getUserCode, withDateTooltip, onStop, onUnstop, onDelete]);


  const total = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const blockIndex = Math.floor((page - 1) / BLOCK_SIZE);     // 현재 블록(0-based)
  const blockStart = blockIndex * BLOCK_SIZE + 1;             // 블록 시작 페이지
  const blockEnd = Math.min(blockStart + BLOCK_SIZE - 1, totalPages); // 블록 끝 페이지

  const showPager = total > PAGE_SIZE;                        // 총 데이터가 한 페이지 초과일 때만 노출

  const pagedUsers = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return filteredUsers.slice(start, end);
  }, [filteredUsers, page]);

  // 필터/검색/데이터 바뀔 때 페이지 1로 리셋 + 범위 보정
  useEffect(() => {
    setPage(1);
  }, [applied]);
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // 🔎 검색 / 초기화
  const handleSearch = () => {
    const cur = getCurrentJoinParams();
    setApplied({
      searchField,
      keyword,
      channel,
      ticketName,
      joinStart: cur.join_start || "",
      joinEnd: cur.join_end || "",
    });
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleReset = () => {
    setSearchField("all");   // all | user_id | nickname
    setKeyword("");
    setChannel("all");       // all | kakao | naver | google | apple
    setTicketName("전체");
    setJoinMode("single");
    setJoinSingle(null);
    setJoinRange({ from: null, to: null });
    setApplied(APPLIED_DEFAULT);
    fetchUsers();         // ← 전체 다시 가져오기
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="mx-auto max-w-[1280px] bg-white rounded-2xl shadow overflow-hidden">
        <div className="flex">
          <div className="flex-1 min-w-0 bg-neutral-50/70">
            <main className="pt-12 flex flex-col p-4 overflow-x-hidden w-full">
              <section className="px-4 py-2 w-full overflow-x-hidden">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-xl font-semibold mb-4">회원정보</h2>
                </div>

                {/* 검색바: user_id 입력 */}
                <div className="rounded-lg bg-white px-3 py-2">
                  <div className="grid grid-cols-1 gap-3 mb-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-neutral-500">검색</label>
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          value={searchField}
                          onChange={(e) => setSearchField(e.target.value)}
                          className="h-9 w-36 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                        >
                          <option value="all">전체</option>
                          <option value="user_id">회원 번호</option>
                          <option value="nickname">표시 이름</option>
                        </select>
                        <input
                          value={keyword}
                          onChange={(e) => setKeyword(e.target.value)}
                          type="text"
                          placeholder={
                            searchField === "user_id" ? "회원 번호 입력" :
                              searchField === "nickname" ? "표시 이름(닉네임) 입력" : "검색어 입력"
                          }
                          className="h-9 w-56 rounded-md border border-neutral-300 bg-white px-3 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-neutral-500">채널</label>
                      <select
                        value={channel}
                        onChange={(e) => setChannel(e.target.value)}
                        className="h-9 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                      >
                        <option value="all">전체</option>
                        <option value="kakao">카카오톡</option>
                        <option value="google">구글</option>
                        <option value="local">로컬</option>
                      </select>
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-neutral-500">상품명</label>
                      <select
                        className="h-9 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                        value={ticketName}
                        onChange={(e) => setTicketName(e.target.value)}
                      >
                        {TICKET_NAME_MAP.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex flex-col gap-1 relative">
                      <label className="text-sm text-neutral-500">가입일</label>
                      <button
                        type="button"
                        onClick={() => setShowJoinPicker(true)}
                        className="h-9 w-[230px] inline-flex items-center justify-between gap-2
                                rounded-md border border-neutral-300 bg-white px-2 text-sm whitespace-nowrap"
                      >
                        <span className="truncate">
                          {(() => {
                            const cur = getCurrentJoinParams();
                            if (cur.join_start && cur.join_end) {
                              const s = new Date(cur.join_start);
                              const e = new Date(cur.join_end);
                              const fmt = (d) =>
                                `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                              return `${fmt(s)} ~ ${fmt(e)}`;
                            }
                            return "-";
                          })()}
                        </span>
                        <Calendar />
                      </button>

                      {/* 달력: 열렸을 때만 렌더 */}
                      {showJoinPicker && (
                        <>
                          <div
                            className="fixed inset-0 bg-black/30 z-[40]"
                            onClick={() => setShowJoinPicker(false)}
                          />
                          <div className="absolute z-[50] mt-2 rounded-lg border bg-white shadow-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <select
                                value={joinMode}
                                onChange={(e) => setJoinMode(e.target.value)}
                                className="h-8 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                              >
                                <option value="single">하루</option>
                                <option value="range">기간</option>
                              </select>
                            </div>

                            <PeriodPicker
                              mode={joinMode}
                              value={joinMode === "single" ? joinSingle : joinRange}
                              onChange={(val) => {
                                if (joinMode === "single") { setJoinSingle(val || null); }
                                else { setJoinRange(val ?? { from: null, to: null }); }
                              }}
                              onConfirm={() => setShowJoinPicker(false)}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-center gap-2 py-2">
                    <button
                      type="button"
                      onClick={handleReset}
                      className="h-9 px-6 rounded-md border border-neutral-300 text-white bg-neutral-500 hover:bg-neutral-400"
                    >
                      초기화
                    </button>
                    <button
                      type="button"
                      onClick={handleSearch}
                      disabled={!isDirty}
                      className={`h-9 px-9 rounded-md text-white shadow-sm  ${isDirty ? "bg-[#7864F9] hover:bg-violet-600" : "bg-violet-400/40 cursor-not-allowed"}`}
                    >
                      검색
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2 py-3">
                  <div className="text-sm text-neutral-600">
                    총 <span className="text-violet-500">{filteredUsers.length} 건</span>
                  </div>
                </div>

                <div className="rounded-xl border bg-white text-sm overflow-hidden flex flex-col h-[55vh] p-4">
                  {loading ? (
                    <div className="p-10 text-center text-gray-500">불러오는 중…</div>
                  ) : users.length === 0 ? (
                    <div className="p-10 text-center text-gray-500">회원정보가 없습니다.</div>
                  ) : (
                    <SortableTable
                      data={pagedUsers}
                      columns={columns}
                      defaultSort={{ key: "created_at", dir: "desc" }}
                      rowKey={(row, i) => `${row.user_id}-${i}`}
                      rowClassName={(row) => "border-t border-gray-100 " + (row.__ghost ? "bg-neutral-50 text-neutral-500" : "")}
                      onRowClick={(row) => navigate(`/admin/users/${row.user_id}`)}
                      headerCellClassName="bg-neutral-50 text-black/50 first:rounded-tl-xl last:rounded-tr-xl text-sm"
                      bodyClassName="text-sm text-black/50"
                      headerPaddingClass="px-2 py-2"
                      cellPaddingClass="px-2 py-2"
                      columnWidths={["90px", "75px", "12rem", "80px", "90px", "8rem", "105px", "105px", "28px"]}
                    />
                  )}
                </div>

                <StopModal
                  open={stopOpen}
                  onClose={() => { setStopOpen(false); setStopTarget(null); }}
                  target={stopTarget}
                  getUserDetail={getUserDetail}
                  getSubLabel={getSubLabel}
                  onDone={fetchUsers}
                />

                <UnstopModal
                  open={unstopOpen}
                  onClose={() => { setUnstopOpen(false); setUnstopTarget(null); }}
                  target={unstopTarget}
                  getUserDetail={getUserDetail}
                  getSubLabel={getSubLabel}
                  onDone={fetchUsers}
                />

                <DeleteModal
                  open={deleteOpen}
                  onClose={() => { setDeleteOpen(false); setDeleteTarget(null); }}
                  target={deleteTarget}
                  getUserDetail={getUserDetail}
                  getSubLabel={getSubLabel}
                  onDone={fetchUsers}
                />

                {showPager && (
                  <div className="flex px-3 py-3 border-t">
                    <div className="flex justify-center items-center gap-1 mx-auto">
                      {totalPages > BLOCK_SIZE && (
                        <button
                          className="h-8 px-3 rounded-md border text-sm disabled:opacity-50"
                          disabled={page === 1}
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                        >
                          이전
                        </button>
                      )}

                      {Array.from({ length: blockEnd - blockStart + 1 }, (_, i) => blockStart + i).map((n) => (
                        <button
                          key={n}
                          className={`h-8 px-3 rounded-md border text-sm ${n === page ? "bg-violet-600 text-white border-violet-600" : ""}`}
                          onClick={() => setPage(n)}
                        >
                          {n}
                        </button>
                      ))}

                      {totalPages > BLOCK_SIZE && (
                        <button
                          className="h-8 px-3 rounded-md border text-sm disabled:opacity-50"
                          disabled={page === totalPages}
                          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        >
                          다음
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </section>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CmsUsersDeleted;
